import { useCallback, useEffect, useId, useRef, useState } from "react"

import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Loader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  toast,
} from "@coval/ui"
import { Check, ListPlus, Plus } from "lucide-react"

import { logger } from "@/lib/logger"
import type { TestCaseInput } from "@/types/TestCaseInput"
import { TestSetType } from "@/types/TestSetInput"

// Matches the per-row cap in DeepDiveTestSetButton; transcripts larger than this
// blow past test-set input limits when serialized.
const MAX_TRANSCRIPT_JSON_LENGTH = 50_000

const log = logger("AddToTestSetButton")

interface AddToTestSetTestSet {
  id: string
  display_name: string
}

interface AddToTestSetButtonProps {
  selectedSimulationOutputIds: string[]
  testSets: AddToTestSetTestSet[] | undefined
  /**
   * Load a single conversation transcript by simulation output id.
   * Return null/undefined for conversations that have no transcript to add.
   */
  fetchTranscript: (simulationOutputId: string) => Promise<unknown>
  addToExistingTestSet: (
    testSetId: string,
    inputs: TestCaseInput[],
  ) => Promise<void>
  createTestSetWithRows: (params: {
    displayName: string
    description: string
    inputs: TestCaseInput[]
  }) => Promise<void>
  isDisabled?: boolean
  onSuccess?: () => void
}

export const AddToTestSetButton = ({
  selectedSimulationOutputIds,
  testSets,
  fetchTranscript,
  addToExistingTestSet,
  createTestSetWithRows,
  isDisabled = false,
  onSuccess,
}: AddToTestSetButtonProps) => {
  const testSetSelectId = useId()
  const testSetNameId = useId()
  const testSetDescId = useId()
  const testSetDescLabelId = useId()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<"existing" | "new">("existing")
  const [selectedTestSet, setSelectedTestSet] = useState("")
  const [newTestSetName, setNewTestSetName] = useState("")
  const [newTestSetDescription, setNewTestSetDescription] = useState("")
  const [isSaving, setIsSaving] = useState(false)

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const selectedCount = selectedSimulationOutputIds.length

  const resetModalState = useCallback(() => {
    setSelectedTestSet("")
    setNewTestSetName("")
    setNewTestSetDescription("")
    setActiveTab("existing")
    setIsSaving(false)
  }, [])

  const handleModalOpenChange = useCallback(
    (open: boolean) => {
      if (isSaving && !open) {
        return
      }
      setIsModalOpen(open)
      if (!open) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }
        // Delay so the modal exit animation can play before form resets.
        timeoutRef.current = setTimeout(() => {
          resetModalState()
          timeoutRef.current = null
        }, 100)
      }
    },
    [isSaving, resetModalState],
  )

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    },
    [],
  )

  const buildTestCaseInputs = useCallback(async (): Promise<{
    inputs: TestCaseInput[]
    skippedOversizeCount: number
    failedFetchCount: number
  }> => {
    // allSettled (not all) so a single deleted or inaccessible conversation
    // doesn't discard every transcript we did fetch successfully.
    const results = await Promise.allSettled(
      selectedSimulationOutputIds.map((id) => fetchTranscript(id)),
    )

    const inputs: TestCaseInput[] = []
    let skippedOversizeCount = 0
    let failedFetchCount = 0

    for (const result of results) {
      if (result.status === "rejected") {
        failedFetchCount += 1
        log.warn("Failed to fetch transcript for bulk add", {
          error:
            result.reason instanceof Error
              ? result.reason.message
              : String(result.reason),
        })
        continue
      }
      const transcript = result.value
      if (transcript == null) {
        continue
      }
      const transcriptJson = JSON.stringify(transcript)
      if (transcriptJson.length > MAX_TRANSCRIPT_JSON_LENGTH) {
        skippedOversizeCount += 1
        continue
      }
      inputs.push({
        input_str: transcriptJson,
        expected_behaviors: [],
        s3_bucket: null,
        s3_key: null,
        input_type: TestSetType.TRANSCRIPT,
        simulation_metadata_input: null,
        metric_input: null,
      })
    }

    return { inputs, skippedOversizeCount, failedFetchCount }
  }, [selectedSimulationOutputIds, fetchTranscript])

  const handleSave = useCallback(async () => {
    if (selectedCount === 0) {
      return
    }
    setIsSaving(true)

    try {
      const { inputs, skippedOversizeCount, failedFetchCount } =
        await buildTestCaseInputs()

      if (inputs.length === 0) {
        const reasons: string[] = []
        if (skippedOversizeCount > 0) {
          reasons.push(
            `${skippedOversizeCount} exceeded the test-set size limit`,
          )
        }
        if (failedFetchCount > 0) {
          reasons.push(`${failedFetchCount} could not be loaded`)
        }
        toast.danger({
          title: "Nothing to add",
          description:
            reasons.length > 0
              ? `None of the selected conversations were added: ${reasons.join(
                  " and ",
                )}.`
              : "The selected conversations have no transcript to add.",
        })
        setIsSaving(false)
        return
      }

      if (activeTab === "existing") {
        if (!selectedTestSet) {
          throw new Error("Please select a test set")
        }
        await addToExistingTestSet(selectedTestSet, inputs)
      } else {
        if (!newTestSetName.trim()) {
          throw new Error("Please provide a test set name")
        }
        await createTestSetWithRows({
          displayName: newTestSetName.trim(),
          description:
            newTestSetDescription.trim() ||
            "Test set created from uploaded conversations",
          inputs,
        })
      }

      const skippedNotes: string[] = []
      if (skippedOversizeCount > 0) {
        skippedNotes.push(`${skippedOversizeCount} transcript too large`)
      }
      if (failedFetchCount > 0) {
        skippedNotes.push(`${failedFetchCount} failed to load`)
      }
      const skippedSuffix = skippedNotes.length
        ? ` (${skippedNotes.join(", ")})`
        : ""
      toast.success({
        title:
          activeTab === "existing" ? "Added to test set" : "Test set created",
        description: `${inputs.length} conversation${
          inputs.length === 1 ? "" : "s"
        } added${skippedSuffix}.`,
      })

      handleModalOpenChange(false)
      onSuccess?.()
    } catch (error) {
      log.error("Failed to add conversations to test set", error)
      toast.danger({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : `Failed to ${
                activeTab === "existing" ? "add to" : "create"
              } test set.`,
      })
      setIsSaving(false)
    }
  }, [
    selectedCount,
    buildTestCaseInputs,
    activeTab,
    selectedTestSet,
    addToExistingTestSet,
    newTestSetName,
    newTestSetDescription,
    createTestSetWithRows,
    handleModalOpenChange,
    onSuccess,
  ])

  const isValid =
    activeTab === "existing" ? !!selectedTestSet : !!newTestSetName.trim()

  return (
    <>
      <Button
        variant="secondary"
        disabled={isDisabled || selectedCount === 0}
        onClick={() => setIsModalOpen(true)}
        data-testid="bulk-add-to-test-set-button"
      >
        <ListPlus className="w-4 h-4" />
        Test set
      </Button>

      <Dialog open={isModalOpen} onOpenChange={handleModalOpenChange}>
        <DialogContent
          className="max-w-2xl"
          data-testid="add-to-test-set-modal"
          onInteractOutside={(e) => {
            if (isSaving) {
              e.preventDefault()
            }
          }}
          onEscapeKeyDown={(e) => {
            if (isSaving) {
              e.preventDefault()
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>
              Add {selectedCount} conversation
              {selectedCount === 1 ? "" : "s"} to test set
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <Tabs
              value={activeTab}
              onValueChange={(value) =>
                setActiveTab(value as "existing" | "new")
              }
            >
              <TabsList className="w-full">
                <TabsTrigger value="existing" className="flex-1">
                  Existing Test Set
                </TabsTrigger>
                <TabsTrigger value="new" className="flex-1">
                  New Test Set
                </TabsTrigger>
              </TabsList>

              <TabsContent value="existing" className="pt-3">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={testSetSelectId}>
                    Select test set{" "}
                    <span className="text-coval-danger-text">*</span>
                  </Label>
                  <Select
                    value={selectedTestSet}
                    onValueChange={setSelectedTestSet}
                  >
                    <SelectTrigger
                      id={testSetSelectId}
                      aria-label="Select test set"
                      data-testid="add-to-test-set-select"
                    >
                      <SelectValue placeholder="Choose a test set" />
                    </SelectTrigger>
                    <SelectContent>
                      {(testSets ?? []).map((testSet) => (
                        <SelectItem
                          key={testSet.id}
                          value={testSet.id}
                          data-testid={`add-to-test-set-option-${testSet.id}`}
                        >
                          {testSet.display_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>

              <TabsContent value="new" className="pt-3">
                <div className="space-y-4">
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor={testSetNameId}>
                      Test Set Name{" "}
                      <span className="text-coval-danger-text">*</span>
                    </Label>
                    <Input
                      id={testSetNameId}
                      placeholder="Enter test set name"
                      value={newTestSetName}
                      onChange={(e) => setNewTestSetName(e.target.value)}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label id={testSetDescLabelId} htmlFor={testSetDescId}>
                      Description
                    </Label>
                    <Textarea
                      id={testSetDescId}
                      aria-labelledby={testSetDescLabelId}
                      placeholder="Enter description (optional)"
                      value={newTestSetDescription}
                      onChange={(e) => setNewTestSetDescription(e.target.value)}
                      rows={3}
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => handleModalOpenChange(false)}
              disabled={isSaving}
              data-testid="add-to-test-set-cancel-button"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                void handleSave()
              }}
              disabled={!isValid || isSaving}
              data-testid="add-to-test-set-save-button"
            >
              {isSaving ? (
                <Loader className="h-4 w-4" />
              ) : activeTab === "existing" ? (
                <Check className="w-4 h-4" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
              {activeTab === "existing" ? "Add to Test Set" : "Create Test Set"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
